// src/modules/app-views/_apps/appmulti/receivables/pages/Document/tabs/RecordTab/index.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { Card, Col, Row } from 'antd';
import { useDocumentContext } from 'components/core/DocumentProvider';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleNumber from 'components/SimpleComponents/SimpleNumber';
import SimpleSelect from 'components/SimpleComponents/SimpleSelect';
import SimpleDatePicker from 'components/SimpleComponents/SimpleDatePicker';
import SimpleSelectAsyncEntities from 'components/SimpleComponents/SimpleSelectAsyncEntities';
import translateX from 'utils/translateX';
import receivablesModuleService from 'modules/app-views/_apps/appmulti/receivables/service';

// *******************************************************************************
// GLOBAL VARIABLES
// *******************************************************************************
const STYLES = {
  container: { position: 'relative', minHeight: 120 },
  cardMargin: { marginBottom: 16 },
};

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const RecordTab = () => {
  // ---------------------------------------------------------------------------
  // PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { data, viewMode } = useDocumentContext();
  const { locked } = useSimpleFormContext();

  // ---------------------------------------------------------------------------
  // HOOKS & REFS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const billingTypeOptions = receivablesModuleService.getBillingTypes();
  const statusOptions = receivablesModuleService.getStatuses();

  // edit lock parity: backup canEditDocument blocks edit once transactionReceiptUrl exists
  const hardLocked = locked || !!data?.transactionReceiptUrl;
  const isNew = viewMode === 'add' || !data?._id;

  // ---------------------------------------------------------------------------
  // LOCAL STATE
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // EFFECTS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // HANDLERS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // LOCAL VARIABLES II
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // UI
  // ---------------------------------------------------------------------------
  return (
    <div className="container" style={STYLES.container}>
      {/* Bill */}
      <Card title={translateX('bill')} style={STYLES.cardMargin}>
        <Row gutter={16}>
          <Col xs={24} sm={12} lg={6}>
            <SimpleNumber
              name="value"
              label="value"
              required
              min={0.01}
              max={9999999.99}
              decimalPlaces={2}
              disabled={hardLocked}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <SimpleDatePicker name="dueDate" label="due_date" required disabled={hardLocked} />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <SimpleSelect
              name="billingType"
              label="payment_method"
              required
              options={billingTypeOptions}
              disabled={hardLocked}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <SimpleNumber
              name="installment"
              label="installment"
              min={1}
              max={999}
              disabled={hardLocked}
            />
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} lg={16}>
            <SimpleInput name="description" label="description" disabled={hardLocked} />
          </Col>
          <Col xs={24} lg={8}>
            <SimpleSelect
              name="status"
              label="status"
              options={statusOptions}
              disabled={hardLocked || isNew}
            />
          </Col>
        </Row>
      </Card>

      {/* Fees (percentage-only — Divergence #5) */}
      <Card title={translateX('interest_and_fines')} style={STYLES.cardMargin}>
        <Row gutter={16}>
          <Col xs={24} sm={8}>
            <SimpleNumber
              name="interest"
              label="monthly_interest"
              min={0}
              max={999.99}
              decimalPlaces={2}
              percentage
              disabled={hardLocked}
            />
          </Col>
          <Col xs={24} sm={8}>
            <SimpleNumber
              name="fine"
              label="fine"
              min={0}
              max={999.99}
              decimalPlaces={2}
              percentage
              disabled={hardLocked}
            />
          </Col>
          <Col xs={24} sm={8}>
            <SimpleNumber
              name="discount"
              label="discount"
              min={0}
              max={999.99}
              decimalPlaces={2}
              percentage
              disabled={hardLocked}
            />
          </Col>
        </Row>
      </Card>

      {/* Customer */}
      <Card title={translateX('customer')} style={STYLES.cardMargin}>
        <Row gutter={16}>
          <Col xs={24} lg={16}>
            <SimpleSelectAsyncEntities
              name="entityId"
              label="drawee"
              required
              entityProfiles={['customer', 'provider', 'collaborator', 'operator']}
              selectedEntity={data?.__customer}
              disabled={hardLocked || !isNew}
            />
          </Col>
        </Row>
      </Card>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default RecordTab;
